import React, {useCallback, useEffect, useState} from 'react';
import {ModelFileUploaded, TreeNode} from '../../../store/modelFile';
import {FileTreeProps, ModelFileAuditedDTOWithButton} from './types';
import {Folders24, Folder24, Development24} from '@carbon/icons-react';
import TableWithSortAndPaginationAntd, {
  Column,
  TO_LOCALE_DATE_STRING_OPTIONS,
  TO_UTC_DATE_STRING_OPTIONS,
} from '../TableWithSortAndPagination';
import {Order, useGridState} from '../../../store/grid';
import {DownOutlined, UpOutlined} from '@ant-design/icons';
import {Select, Checkbox as AntCheckbox, Input} from 'antd';
import {useModelMetadataActiveOptions} from '../../../store/modelMetadata';

export type DirNodeAntdProps<N> = FileTreeProps<N> & {
  isRoot?: boolean;
  node: TreeNode;
  externalModel?: boolean;
  webModel?: boolean;
  theoremModel?: boolean;
  createFlow?: boolean;
  uploadFlow?: boolean;
  cloneFlow?: boolean;
  setSpecificFiles?: (uuids: string[], checked: boolean) => void;
  unSelectAll?: boolean;
};

const Icon = Development24;

const FileTypeTag: React.FC<{file: ModelFileAuditedDTOWithButton}> = ({file}) => (
  <>
    <span>{file.fileTag}</span>
  </>
);

const FileNameTag: React.FC<{file: ModelFileAuditedDTOWithButton}> = ({file}) => (
  <>
    <Icon className="nodeIcon" />
    <span>{file.name}</span>
  </>
);

const displayFileName = (file: ModelFileAuditedDTOWithButton) => <FileNameTag file={file} />;
const displayFileType = (file: ModelFileAuditedDTOWithButton) => <FileTypeTag file={file} />;

function DirNodeAntd<N extends TreeNode>(props: DirNodeAntdProps<N>) {
  const {
    files,
    node,
    isRoot = false,
    onDrop,
    renderButtons,
    defaultFolderOpen = true,
    onFolderOpen,
    onRowClick,
    externalModel,
    webModel,
    theoremModel,
    createFlow,
    uploadFlow,
    cloneFlow,
    handleFileTag,
    setSpecificFiles,
    unSelectAll,
  } = props;

  const options = useModelMetadataActiveOptions();
  // const ALPHA_NUMERIC_DASH_REGEX = /^[a-zA-Z]+$/;
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    if (unSelectAll) {
      setSelectAll(false);
    }
  }, [unSelectAll]);

  const displayDropdown = (file: ModelFileAuditedDTOWithButton) => (
    <>{cloneFlow ? clonedModelFileTags(file) : fileTagSelect(file)}</>
  );

  const fileTagSelect = (file: ModelFileAuditedDTOWithButton) => {
    return (
      <Select
        style={{width: 200}}
        value={file.fileTag || undefined}
        placeholder="Select or type file type"
        getPopupContainer={() =>
          (document.querySelector('.MuiDrawer-paper.whiteTheme') as HTMLElement) || document.body
        }
        listHeight={320}
        optionFilterProp="label"
        onChange={(value: string) => {
          if (value && handleFileTag) {
            const replacedValue = value.replace(' (Create New)', '');
            handleFileTag(file as ModelFileUploaded, replacedValue);
          }
        }}
        showSearch
        filterOption={(input, option) => {
          return (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
        }}
        options={(options?.fileTag || []).map((tag) => ({label: tag, value: tag}))}
        popupRender={(menu) => (
          <>
            {menu}
            <div style={{padding: '8px', borderTop: '1px solid #f0f0f0'}}>
              <Input
                placeholder="Create new file type"
                onPressEnter={(e: any) => {
                  const newValue = e.target.value;
                  if (newValue && handleFileTag) {
                    handleFileTag(file as ModelFileUploaded, newValue);
                  }
                }}
              />
            </div>
          </>
        )}
      />
    );
  };

  const clonedModelFileTags = (file: ModelFileAuditedDTOWithButton) => {
    return !file.uploaded ? fileTagSelect(file) : <Input disabled style={{width: 200}} value={file.fileTag} />;
  };

  const filesGridColumns: Column<ModelFileAuditedDTOWithButton>[] = [
    {id: 'name', label: 'File Name', sortable: false, value: (o) => o.name, display: displayFileName},
    {
      id: 'updatedOn',
      label: 'UpdatedOn',
      sortable: true,
      value: (es) => es.updatedOn,
      display: (es) => es.updatedOn?.toLocaleDateString('en-US', TO_LOCALE_DATE_STRING_OPTIONS),
    },
    {
      id: 'updatedBy',
      label: 'Updated By',
      customHeader: {
        heading: 'UpdatedBy',
      },
      sortable: false,
      value: (es) => es.updatedBy || '',
    },
    {
      id: 'fileTag',
      label: 'What type of file is this?',
      sortable: false,
      value: (es) => es.fileTag || '',
      display: displayFileType,
    },
    {
      id: 'button',
      label: 'Delete',
      customHeader: {
        heading: (
          <div style={{textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
            Delete
          </div>
        ),
      },
      sortable: false,
      value: (es) => es.button || '',
      display: (es) => <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>{es.button}</div>,
    },
  ];

  const createFlowColumns: Column<ModelFileAuditedDTOWithButton>[] = [
    filesGridColumns[0],
    filesGridColumns[1],
    filesGridColumns[2],
    {
      id: 'fileType',
      label: 'File Type',
      sortable: false,
      value: (es) => es.fileTag || '',
      display: displayDropdown,
    },
  ];

  const uploadFlowColumns: Column<ModelFileAuditedDTOWithButton>[] = [
    filesGridColumns[0],
    createFlowColumns[3],
    {
      id: 'selected',
      label: 'Select all',
      customHeader: {
        heading: (
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'}}>
            <span style={{fontSize: '14px'}}>Select all</span>
            <AntCheckbox
              checked={selectAll}
              onChange={(e: any) => {
                const checked = e.target.checked;
                setSelectAll(checked);
                if (setSpecificFiles) {
                  setSpecificFiles(
                    childrenFiles.map((file) => file.uuid),
                    checked,
                  );
                }
              }}
            />
          </div>
        ),
      },
      sortable: false,
      value: (es) => es.selected || '',
      display: (es) => {
        if (es.selected && typeof es.selected === 'object' && 'props' in es.selected) {
          const checkbox = es.selected as any;
          const originalOnChange = checkbox.props?.onChange;
          if (originalOnChange) {
            const wrappedCheckbox = React.cloneElement(checkbox, {
              onChange: (e: any) => {
                if (!e.target.checked && selectAll) {
                  setSelectAll(false);
                } else if (e.target.checked) {
                  const allSelected = (childrenFiles as any[]).every((file: any) => {
                    return file?.selected?.props?.checked;
                  });
                  if (allSelected) {
                    setSelectAll(true);
                  }
                }
                originalOnChange(e);
              },
            });
            return (
              <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>{wrappedCheckbox}</div>
            );
          }
        }
        return <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>{es.selected}</div>;
      },
    },
    filesGridColumns[4],
  ];

  const cloneFlowColumns: Column<ModelFileAuditedDTOWithButton>[] = [
    filesGridColumns[0],
    filesGridColumns[1],
    filesGridColumns[2],
    {
      id: 'button',
      label: 'Select Files',
      customHeader: {
        heading: 'Select Files',
      },
      sortable: false,
      value: (es) => es.button || '',
      display: (es) => <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>{es.button}</div>,
    },
  ];

  const externalModelColumns: Column<ModelFileAuditedDTOWithButton>[] = [
    filesGridColumns[0],
    {
      id: 'updatedOn',
      label: 'Updated On"',
      sortable: true,
      value: (es) => es.updatedOn,
      display: (es) => es.updatedOn?.toLocaleDateString('en-US', TO_UTC_DATE_STRING_OPTIONS),
    },
    filesGridColumns[2],
  ];

  const [isOpen, setOpen] = useState(
    isRoot ||
      defaultFolderOpen === true ||
      (typeof defaultFolderOpen === 'string' && defaultFolderOpen.startsWith(node.path)),
  );
  const gridState = useGridState({orderBy: 'updatedOn', order: Order.ASC});
  const {path, name} = node;

  const currentPathLength = path.length + (isRoot ? 0 : 1);

  const directories: {[dirName: string]: TreeNode} = {};
  const childrenFiles: N[] = [];

  files
    .filter((childNode) => childNode.path.startsWith(isRoot ? '' : `${path}/`))
    .forEach((childNode) => {
      const restPath = childNode.path.substring(currentPathLength);
      const slashIndex = restPath.indexOf('/');
      if (slashIndex === -1) {
        if (childNode.folder) {
          directories[childNode.name] = childNode;
        } else {
          childNode.button = renderButtons && renderButtons(childNode);
          childrenFiles.push(childNode);
        }
        return;
      }
      const dirName = restPath.substring(0, slashIndex);
      if (dirName === '' && childNode.folder) {
        childNode.button = renderButtons && renderButtons(childNode);
        directories[childNode.name] = childNode;
      }
      if (dirName !== '' && !directories[dirName]) {
        const pathName = `${path}/`;
        const dirPath = `${path === '' ? '' : pathName}${dirName}`;
        directories[dirName] = {
          uploaded: false,
          folder: true,
          name: dirName,
          path: dirPath,
          uuid: dirPath,
        };
      }
    });

  const handleToggleOpen = useCallback(() => {
    if (!isOpen) {
      onFolderOpen?.(node);
    }
    setOpen(!isOpen);
  }, [isOpen, onFolderOpen, node]);

  let FolderIcon = Folder24;
  if (isOpen && childrenFiles.length === 0 && Object.values(directories).length === 0 && node.contentLoaded !== false) {
    FolderIcon = Folders24;
  } else if (isOpen && node.contentLoaded === false) {
    FolderIcon = Folders24;
  } else if (isOpen) {
    FolderIcon = Folder24;
  }

  const arrows = useCallback(
    (isOpen: boolean) => {
      return isOpen ? (
        <DownOutlined onClick={handleToggleOpen} style={{cursor: 'pointer'}} />
      ) : (
        <UpOutlined onClick={handleToggleOpen} style={{cursor: 'pointer'}} />
      );
    },
    [handleToggleOpen],
  );

  const displayCloneColumns = () => {
    return cloneFlow ? cloneFlowColumns : filesGridColumns;
  };
  const displayUploadColumns = () => {
    return uploadFlow ? uploadFlowColumns : displayCloneColumns();
  };
  const displayCreateColumns = () => {
    return createFlow ? createFlowColumns : displayUploadColumns();
  };

  return (
    <>
      {isRoot && (webModel || theoremModel) && (
        <>
          <FolderIcon onClick={handleToggleOpen} className="nodeIcon rootNodeIcon" />
          {arrows(isOpen)}
        </>
      )}
      {!isRoot && (
        <div className="treeNode">
          <FolderIcon onClick={handleToggleOpen} className="nodeIcon" />
          <span data-testid="file-name" className="fileName">
            {name} {arrows(isOpen)}
          </span>
        </div>
      )}
      {isOpen && (
        <ol>
          {Object.values(directories).map((childNode) => {
            return (
              <li
                key={childNode.uuid}
                onDrop={(event: React.DragEvent) => {
                  if (onDrop) {
                    event.stopPropagation();
                    onDrop(event, childNode);
                  }
                }}
              >
                <DirNodeAntd<N>
                  files={files}
                  handleFileTag={handleFileTag}
                  node={childNode}
                  webModel={webModel}
                  onDrop={onDrop}
                  renderButtons={renderButtons}
                  defaultFolderOpen={defaultFolderOpen}
                  onFolderOpen={onFolderOpen}
                  onRowClick={onRowClick}
                  setSpecificFiles={setSpecificFiles}
                  createFlow={createFlow}
                  uploadFlow={uploadFlow}
                  cloneFlow={cloneFlow}
                  unSelectAll={unSelectAll}
                />
              </li>
            );
          })}
          {childrenFiles.length !== 0 && (
            <TableWithSortAndPaginationAntd<ModelFileAuditedDTOWithButton>
              getRowId={(o) => o.uuid}
              onClickRow={onRowClick}
              externalModel={externalModel}
              // @ts-expect-error: adjusting the type error
              rows={childrenFiles}
              columns={externalModel ? externalModelColumns : displayCreateColumns()}
              hidePagination={true}
              gridState={gridState}
            />
          )}
        </ol>
      )}
    </>
  );
}

export default DirNodeAntd;
